import React, { useState, useEffect, useCallback, useRef } from "react";
import { Unity, useUnityContext } from "react-unity-webgl";
import "./Loading.css";
import "./Background.css";
import { sendAnalyticsEvent } from "./analytics";

function UnityGame() {
  const videoRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  const cameraReadyRef = useRef(false);
  const frameIntervalRef = useRef(null);

  // ✅ Gallery / explorer picker
  const fileInputRef = useRef(null);

  // ✅ Optional: container observer (robust sizing)
  const containerRef = useRef(null);

  // ✅ Keep latest sendMessage ref-safe for window functions
  const sendMessageRef = useRef(null);

  // ✅ PC FIX: prevent resize “fight” due to RAF + timeouts
  const forcedDesktopOnceRef = useRef(false);

  // ✅ ANALYTICS ADD: loading tracking refs + safe sender
  const loadingStartMsRef = useRef(null);
  const loadingStartedSentRef = useRef(false);

  const safeSendLoadingAnalytics = useCallback((eventName, params) => {
    try {
      sendAnalyticsEvent(eventName, params || {});
    } catch (e) {
      console.warn("sendAnalyticsEvent failed:", e);
    }
  }, []);

  useEffect(() => {
    cameraReadyRef.current = cameraReady;
  }, [cameraReady]);

  const { unityProvider, isLoaded, loadingProgression, sendMessage } =
    useUnityContext({
      loaderUrl: "UnityBuild/WebGL.loader.js",
      dataUrl: "UnityBuild/WebGL.data.unityweb",
      frameworkUrl: "UnityBuild/WebGL.framework.js.unityweb",
      codeUrl: "UnityBuild/WebGL.wasm.unityweb",
      companyName: "Freakout",
      productName: "FibeMini",
      productVersion: "0.1",
      devicePixelRatio: window.devicePixelRatio || 1,
    });

  // ✅ Keep sendMessage safe even for window.* usage
  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  // ✅ Reset per reload
  useEffect(() => {
    if (!isLoaded) forcedDesktopOnceRef.current = false;
  }, [isLoaded]);

  // ✅ Reset loading analytics per reload
  useEffect(() => {
    if (!isLoaded) {
      loadingStartedSentRef.current = false;
      loadingStartMsRef.current = null;
    }
  }, [isLoaded]);

  const checkPlatform = useCallback(() => {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  }, []);

  // ✅ loading start
  useEffect(() => {
    if (isLoaded) return;

    if (!loadingStartedSentRef.current) {
      loadingStartedSentRef.current = true;
      loadingStartMsRef.current = Date.now();

      safeSendLoadingAnalytics("loading_page", {
        progress: Math.round((loadingProgression || 0) * 100),
        dpr: window.devicePixelRatio || 1,
        userAgent: navigator.userAgent,
        ts: new Date().toISOString(),
      });
    }
  }, [isLoaded, loadingProgression, safeSendLoadingAnalytics]);

  // ✅ loading done + time
  useEffect(() => {
    if (!isLoaded) return;

    const endMs = Date.now();
    const startMs = loadingStartMsRef.current || endMs;
    const elapsedMs = Math.max(0, endMs - startMs);

    safeSendLoadingAnalytics("loading_done", {
      progress: 100,
      elapsedMs,
      ts: new Date().toISOString(),
    });

    safeSendLoadingAnalytics("loading_time", {
      valueMs: elapsedMs,
      valueSec: Math.round(elapsedMs / 1000),
      ts: new Date().toISOString(),
    });
  }, [isLoaded, safeSendLoadingAnalytics]);

  // -------------------- UNITY ↔ REACT BRIDGE FOR SCREENSHOT DOWNLOAD --------------------
  useEffect(() => {
    window.onUnityScreenshot = (base64) => {
      const now = new Date();

      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");

      const hour = String(now.getHours()).padStart(2, "0");
      const minute = String(now.getMinutes()).padStart(2, "0");
      const second = String(now.getSeconds()).padStart(2, "0");

      const fileName = `fibemini-${year}-${month}-${day}-${hour}-${minute}-${second}.png`;

      const link = document.createElement("a");
      link.href = "data:image/png;base64," + base64;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    };
  }, []);

  useEffect(() => {
    window.isReactCameraReady = function () {
      return typeof window.startCamera === "function";
    };
  }, []);

  // -------------------- Shared canvas + helpers (independent of camera) --------------------
  const sharedCanvasRef = useRef(null);
  const sharedCtxRef = useRef(null);
  const isMobileRef = useRef(false);

  useEffect(() => {
    if (!sharedCanvasRef.current) {
      const c = document.createElement("canvas");
      const ctx = c.getContext("2d");
      sharedCanvasRef.current = c;
      sharedCtxRef.current = ctx;
    }
    isMobileRef.current = checkPlatform();
  }, [checkPlatform]);

  // ---------- HELPERS ----------
  const canvasToBlob = (canvas, type, quality) =>
    new Promise((resolve) => canvas.toBlob(resolve, type, quality));

  const blobToDataURL = (blob) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onerror = reject;
      r.onload = () => resolve(r.result);
      r.readAsDataURL(blob);
    });

  const compressCanvasToDataURLUnderBytes = useCallback(
    async (
      srcCanvas,
      targetBytes,
      {
        type = "image/jpeg",
        qualityStart = 0.92,
        qualityMin = 0.25,
        qualityStep = 0.06,
        allowDownscale = true,
        scaleStep = 0.9,
        scaleMin = 0.75,
      } = {},
    ) => {
      const work = document.createElement("canvas");
      const ctx = work.getContext("2d");

      const tryAtScale = async (scale) => {
        const w = Math.max(1, Math.round(srcCanvas.width * scale));
        const h = Math.max(1, Math.round(srcCanvas.height * scale));

        work.width = w;
        work.height = h;
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(srcCanvas, 0, 0, w, h);

        let best = null;

        for (let q = qualityStart; q >= qualityMin; q -= qualityStep) {
          const blob = await canvasToBlob(work, type, q);
          if (!blob) continue;

          if (!best || blob.size < best.blob.size)
            best = { blob, q, w, h, scale };

          if (blob.size <= targetBytes) {
            const dataUrl = await blobToDataURL(blob);
            return {
              ok: true,
              dataUrl,
              blob,
              quality: q,
              width: w,
              height: h,
              scale,
            };
          }
        }

        return best ? { ok: false, ...best } : null;
      };

      let bestOverall = await tryAtScale(1.0);
      if (bestOverall?.ok) return bestOverall;

      if (allowDownscale) {
        let scale = scaleStep;
        while (scale >= scaleMin) {
          const res = await tryAtScale(scale);
          if (res?.ok) return res;

          if (
            res?.blob &&
            (!bestOverall?.blob || res.blob.size < bestOverall.blob.size)
          ) {
            bestOverall = res;
          }

          scale *= scaleStep;
        }
      }

      if (bestOverall?.blob) {
        const dataUrl = await blobToDataURL(bestOverall.blob);
        return {
          ok: false,
          dataUrl,
          blob: bestOverall.blob,
          quality: bestOverall.q,
          width: bestOverall.w,
          height: bestOverall.h,
          scale: bestOverall.scale,
        };
      }

      throw new Error("Compression failed: could not produce JPEG blob.");
    },
    [],
  );

  const loadImageFromFile = (file) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(e);
      };
      img.src = url;
    });

  function drawFitNoCrop({ source, srcW, srcH, maxW, maxH }) {
    const canvas = sharedCanvasRef.current;
    const ctx = sharedCtxRef.current;
    if (!canvas || !ctx) return { outW: 0, outH: 0, scale: 1 };

    // Fit INSIDE box (no crop). Never upscale.
    const scale = Math.min(1, maxW / srcW, maxH / srcH);

    const outW = Math.max(1, Math.round(srcW * scale));
    const outH = Math.max(1, Math.round(srcH * scale));

    canvas.width = outW;
    canvas.height = outH;

    ctx.save();
    ctx.clearRect(0, 0, outW, outH);
    ctx.drawImage(source, 0, 0, srcW, srcH, 0, 0, outW, outH);
    ctx.restore();

    return { outW, outH, scale };
  }

  function cropToFillAndDraw({ source, srcW, srcH, dstW, dstH, mirror }) {
    const canvas = sharedCanvasRef.current;
    const ctx = sharedCtxRef.current;
    if (!canvas || !ctx) return;

    const srcAspect = srcW / srcH;
    const dstAspect = dstW / dstH;

    let sx = 0,
      sy = 0,
      sw = srcW,
      sh = srcH;

    if (srcAspect > dstAspect) {
      sw = srcH * dstAspect;
      sx = (srcW - sw) / 2;
    } else {
      sh = srcW / dstAspect;
      sy = (srcH - sh) / 2;
    }

    canvas.width = dstW;
    canvas.height = dstH;

    ctx.save();
    ctx.clearRect(0, 0, dstW, dstH);

    if (mirror) {
      ctx.scale(-1, 1);
      ctx.drawImage(source, sx, sy, sw, sh, -dstW, 0, dstW, dstH);
    } else {
      ctx.drawImage(source, sx, sy, sw, sh, 0, 0, dstW, dstH);
    }

    ctx.restore();
  }

  function sendEachFrameToUnity({ base64, width, height, isMobile, source, extra }) {
    const sm = sendMessageRef.current;
    if (!sm) return;

    const payload = JSON.stringify({
      base64,
      width,
      height,
      isMobile,
      source,
      ...(extra || {}),
    });

    sm("CameraWebController", "OnTextureReceived", payload);
  }

  function sendGalleryToUnity({ base64, width, height, isMobile, source, extra }) {
    const sm = sendMessageRef.current;
    if (!sm) return;

    const payload = JSON.stringify({
      base64,
      width,
      height,
      isMobile,
      source,
      ...(extra || {}),
    });

    sm("CameraWebController", "OnTextureReceivedFromGallery", payload);
  }

  // -------------------- GALLERY PICKER --------------------
  useEffect(() => {
    if (!isLoaded) return;

    window.pickFromGallery = (width, height) => {
      const input = fileInputRef.current;
      if (!input) {
        sendMessageRef.current?.(
          "CameraWebController",
          "OnErrorReact",
          "File input missing",
        );
        return;
      }

      input.value = "";
      input.accept = "image/jpeg,image/png,image/webp,image/heic,image/heif";
      input.removeAttribute("capture");

      input.onchange = async () => {
        try {
          const file = input.files?.[0];
          if (!file) return;

          const extOk = /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);
          if (!extOk) {
            sendMessageRef.current?.(
              "CameraWebController",
              "OnErrorReact",
              "Unsupported image format. Please choose JPG/PNG/WebP/HEIC.",
            );
            return;
          }

          let img;
          try {
            img = await loadImageFromFile(file);
          } catch {
            sendMessageRef.current?.(
              "CameraWebController",
              "OnErrorReact",
              "This image format can't be opened in this browser. Please export as JPG.",
            );
            return;
          }

          const srcW = img.naturalWidth || img.width;
          const srcH = img.naturalHeight || img.height;

          // Keep FULL image (no crop). Fit inside the requested box (width/height) * 2, no upscale.
          const maxW = (width || srcW) * 2;
          const maxH = (height || srcH) * 2;

          drawFitNoCrop({
            source: img,
            srcW,
            srcH,
            maxW,
            maxH,
          });

          const canvas = sharedCanvasRef.current;
          const TARGET_BYTES = 500 * 1024;

          const compressed = await compressCanvasToDataURLUnderBytes(
            canvas,
            TARGET_BYTES,
            {
              qualityStart: 0.92,
              qualityMin: 0.5,
              qualityStep: 0.06,
              allowDownscale: true,
              scaleStep: 0.9,
              scaleMin: 0.75,
            },
          );

          sendGalleryToUnity({
            base64: compressed.dataUrl,
            width: compressed.width,
            height: compressed.height,
            isMobile: true,
            source: "gallery",
            extra: {
              fileName: file.name,
              inputMime: file.type || "",
              compressedBytes: compressed.blob.size,
              jpegQuality: compressed.quality,
              scale: compressed.scale,
            },
          });
        } catch (err) {
          console.error("Gallery error:", err);
          sendMessageRef.current?.(
            "CameraWebController",
            "OnErrorReact",
            err?.message || String(err),
          );
        }
      };

      input.click();
    };

    return () => {
      if (window.pickFromGallery) delete window.pickFromGallery;
    };
  }, [isLoaded, compressCanvasToDataURLUnderBytes]);

  // -------------------- CAMERA STREAM --------------------
  useEffect(() => {
    if (!isLoaded) return;

    if (!window.startCamera) {
      window.startCamera = async (width, height) => {
        try {
          const isMobile = checkPlatform();
          isMobileRef.current = isMobile;

          let stream;
          const video = videoRef.current;
          if (!video) return;

          if (!isMobile) {
            stream = await navigator.mediaDevices.getUserMedia({ video: true });
          } else {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter((d) => d.kind === "videoinput");

            const backCamera = videoDevices.find((d) =>
              d.label.toLowerCase().includes("back"),
            );

            if (backCamera) {
              stream = await navigator.mediaDevices.getUserMedia({
                video: { deviceId: backCamera.deviceId },
              });
            } else {
              stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { exact: "environment" } },
              });
            }
          }

          video.srcObject = stream;

          video.onloadedmetadata = async () => {
            await video.play();
            setCameraReady(true);

            sendMessageRef.current?.(
              "CameraWebController",
              "OnCameraStarted",
              JSON.stringify({ success: 1 }),
            );

            startSendingFrames(video, width, height);
          };
        } catch (err) {
          console.error("Camera error:", err);
          sendMessageRef.current?.(
            "CameraWebController",
            "OnCameraDisableReact",
            err?.message || String(err),
          );
        }
      };
    }

    if (!window.stopCamera) {
      window.stopCamera = () => {
        const video = videoRef.current;
        if (!video) return;

        const stream = video.srcObject;
        if (stream) stream.getTracks().forEach((track) => track.stop());

        video.srcObject = null;

        if (frameIntervalRef.current) {
          clearInterval(frameIntervalRef.current);
          frameIntervalRef.current = null;
        }

        setCameraReady(false);
      };
    }

    function startSendingFrames(video, width, height) {
      if (frameIntervalRef.current) {
        clearInterval(frameIntervalRef.current);
        frameIntervalRef.current = null;
      }

      frameIntervalRef.current = setInterval(() => {
        if (!video || video.readyState < 2) return;

        const fixedW = width * 2;
        const fixedH = height * 2;

        const srcW = video.videoWidth;
        const srcH = video.videoHeight;

        const mirror = !isMobileRef.current;

        cropToFillAndDraw({
          source: video,
          srcW,
          srcH,
          dstW: fixedW,
          dstH: fixedH,
          mirror,
        });

        const canvas = sharedCanvasRef.current;
        const outBase64 = canvas.toDataURL("image/jpeg", 0.7);

        sendEachFrameToUnity({
          base64: outBase64,
          width: canvas.width,
          height: canvas.height,
          isMobile: isMobileRef.current,
          source: "camera",
          extra: {},
        });
      }, 100);
    }

    return () => {
      if (window.startCamera) delete window.startCamera;
      if (window.stopCamera) delete window.stopCamera;
    };
  }, [isLoaded, checkPlatform]);

  // -------------------- LOADING PLAYLIST (PUBLIC ASSETS) --------------------
  const MAX_LOADING_MEDIA = 5;
  const loadingBase = `${process.env.PUBLIC_URL || ""}/assets/loading`;

  const [loadingIndex, setLoadingIndex] = useState(() => {
    return Math.floor(Math.random() * MAX_LOADING_MEDIA) + 1;
  });

  const [canPlayWebm, setCanPlayWebm] = useState(true);
  const [videoReady, setVideoReady] = useState(false);

  // poster always immediate
  const loadingPoster = `${loadingBase}/video-${1}.jpeg`;
  const loadingWebm = `${loadingBase}/video-${loadingIndex}.webm`;

  useEffect(() => {
    setVideoReady(false);
    setCanPlayWebm(true);
  }, [loadingIndex]);

  // ✅ Unity shows immediately when loaded
  const readyToShowUnity = isLoaded;

  // ✅ Required: send VideoDone once after Unity loaded (1s delay)
  const VIDEO_DONE_AFTER_LOAD_MS = 1000;
  const videoDoneSentRef = useRef(false);
  const videoDoneTimeoutRef = useRef(null);

  useEffect(() => {
    if (!isLoaded) {
      videoDoneSentRef.current = false;
      if (videoDoneTimeoutRef.current) {
        clearTimeout(videoDoneTimeoutRef.current);
        videoDoneTimeoutRef.current = null;
      }
      return;
    }

    if (videoDoneSentRef.current) return;

    videoDoneTimeoutRef.current = setTimeout(() => {
      if (videoDoneSentRef.current) return;
      videoDoneSentRef.current = true;

      try {
        sendMessageRef.current?.("CameraWebController", "VideoDone", "1");
      } catch (e) {
        console.warn("VideoDone sendMessage failed:", e);
      }
    }, VIDEO_DONE_AFTER_LOAD_MS);

    return () => {
      if (videoDoneTimeoutRef.current) {
        clearTimeout(videoDoneTimeoutRef.current);
        videoDoneTimeoutRef.current = null;
      }
    };
  }, [isLoaded]);

  // ✅ Unload loading video when Unity is shown (free memory)
  useEffect(() => {
    if (!readyToShowUnity) return;

    const v = document.getElementById("loading-video");
    if (v) {
      try {
        v.pause();
        const src = v.querySelector("source");
        if (src) src.removeAttribute("src");
        v.removeAttribute("src");
        v.load();
      } catch {}
    }
  }, [readyToShowUnity]);

  // -------------------- LAYOUT --------------------
  // ✅ FULL progress 0..100 (NO 0.9 CAP)
  const progress = Math.min(
    100,
    Math.max(0, Math.round((loadingProgression || 0) * 100)),
  );

  const [showFlipOverlay, setShowFlipOverlay] = useState(false);
  const inputRef = useRef(null);
  const [target, setTarget] = useState({ gameObj: "", method: "" });
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  const viewportHeightRef = useRef(window.innerHeight);
  const [devicePixelRatio, setDevicePixelRatio] = useState(
    window.devicePixelRatio,
  );
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const getViewportSize = useCallback(() => {
    if (!window.visualViewport) {
      return { width: window.innerWidth, height: window.innerHeight };
    }
    if (keyboardOpen) {
      return { width: window.visualViewport.width, height: window.innerHeight };
    } else {
      return {
        width: window.visualViewport.width,
        height: window.visualViewport.height,
      };
    }
  }, [keyboardOpen]);

  const calculateSize = useCallback(() => {
    const { width: screenWidth, height: screenHeight } = getViewportSize();

    let width = screenWidth;
    let height = screenHeight;
    const isMobile = checkPlatform();

    if (isMobile) {
      const orientation = screenWidth > screenHeight ? "landscape" : "portrait";
      setShowFlipOverlay(orientation === "landscape");
    } else {
      const targetRatio = 9 / 16;
      height = screenWidth / targetRatio;
      if (height > screenHeight) {
        height = screenHeight;
        width = height * targetRatio;
      }
      setShowFlipOverlay(false);
    }

    setDimensions({ width, height });

    const canvas = document.querySelector("#unity-canvas");
    const dpr = window.devicePixelRatio || 1;

    if (canvas) {
      if (isMobile) {
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        canvas.width = Math.floor(width * dpr);
        canvas.height = Math.floor(height * dpr);
      } else {
        if (forcedDesktopOnceRef.current) return;
        forcedDesktopOnceRef.current = true;

        canvas.style.width = `${1080}px`;
        canvas.style.height = `${1920}px`;
        canvas.width = 1080;
        canvas.height = 1920;
      }
    }
  }, [getViewportSize, checkPlatform]);

  useEffect(() => {
    calculateSize();
    window.addEventListener("resize", calculateSize);
    return () => window.removeEventListener("resize", calculateSize);
  }, [calculateSize]);

  useEffect(() => {
    if (!isLoaded) return;

    calculateSize();
    requestAnimationFrame(() => calculateSize());
    const t1 = setTimeout(() => calculateSize(), 150);
    const t2 = setTimeout(() => calculateSize(), 500);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [isLoaded, calculateSize]);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const onVV = () => calculateSize();
    vv.addEventListener("resize", onVV);
    vv.addEventListener("scroll", onVV);

    return () => {
      vv.removeEventListener("resize", onVV);
      vv.removeEventListener("scroll", onVV);
    };
  }, [calculateSize]);

  useEffect(() => {
    if (!containerRef.current) return;

    const ro = new ResizeObserver(() => calculateSize());
    ro.observe(containerRef.current);

    return () => ro.disconnect();
  }, [calculateSize]);

  useEffect(() => {
    const updatePixelRatio = () =>
      setDevicePixelRatio(window.devicePixelRatio || 1);
    const mediaMatcher = window.matchMedia(
      `screen and (resolution: ${devicePixelRatio}dppx)`,
    );
    mediaMatcher.addEventListener("change", updatePixelRatio);
    return () => mediaMatcher.removeEventListener("change", updatePixelRatio);
  }, [devicePixelRatio]);

  useEffect(() => {
    if (isLoaded) {
      window.unityAnalyticsEvent = function (eventName, eventParamsJson) {
        let params = {};
        try {
          params = JSON.parse(eventParamsJson);
        } catch (e) {
          console.error("Invalid JSON from Unity:", e);
        }
        sendAnalyticsEvent(eventName, params);
      };
    }
  }, [isLoaded]);

  useEffect(() => {
    if (isLoaded) {
      window.openUnityKeyboard = (gameObjectName, callbackMethod) => {
        setTarget({ gameObj: gameObjectName, method: callbackMethod });

        if (inputRef.current) {
          inputRef.current.value = "";
          inputRef.current.focus();
        }
      };
    }
  }, [isLoaded]);

  useEffect(() => {
    const handleResize = () => {
      const isPortrait = window.innerHeight > window.innerWidth;

      if (!isPortrait) {
        setKeyboardOpen(false);
        if (inputRef.current) inputRef.current.blur();
        return;
      }

      if (window.visualViewport) {
        const diff = viewportHeightRef.current - window.visualViewport.height;
        if (diff > 150) setKeyboardOpen(true);
        else {
          setKeyboardOpen(false);
          if (inputRef.current) inputRef.current.blur();
        }
      }
    };

    window.visualViewport?.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.visualViewport?.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="bg-image"
      style={{ position: "relative", overflow: "hidden" }}
    >
      {/* hidden camera video element */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          position: "absolute",
          opacity: 0,
          width: 1,
          height: 1,
          zIndex: -1,
          pointerEvents: "none",
        }}
      />

      {/* hidden file input for gallery */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        style={{ display: "none" }}
      />

      <Unity
        unityProvider={unityProvider}
        id="unity-canvas"
        className={`w-full transition-opacity duration-500 ${
          readyToShowUnity ? "opacity-100" : "opacity-0"
        }`}
        style={{
          width: dimensions.width,
          height: dimensions.height,
          position: "absolute",
          transition: "opacity 0.5s ease",
        }}
        devicePixelRatio={devicePixelRatio}
      />

      {/* ✅ LOADING OVERLAY (ONLY UNTIL UNITY LOADED) */}
      {!readyToShowUnity && (
        <div className="loader-wrapper">
          <div className="loading-media">
            {/* poster always immediate */}
            <img
              className={`loading-media-image ${videoReady ? "fade-out" : ""}`}
              src={loadingPoster}
              alt="Loading"
              onError={() => console.error("Poster missing:", loadingPoster)}
            />

            {/* video fades in when ready (rotates ONLY on ended, so ended always fires) */}
            {canPlayWebm && (
              <video
                id="loading-video"
                key={loadingIndex}
                className={`loading-media-video ${videoReady ? "fade-in" : ""}`}
                autoPlay
                muted
                playsInline
                preload="auto"
                onCanPlay={() => setVideoReady(true)}
                onError={() => setCanPlayWebm(false)}
                onEnded={() => {
                  // Unity still loading → rotate to next clip only AFTER full play
                  setLoadingIndex((prev) => {
                    let next = Math.floor(Math.random() * MAX_LOADING_MEDIA) + 1;
                    if (next === prev) next = (prev % MAX_LOADING_MEDIA) + 1;
                    return next;
                  });
                }}
              >
                <source src={loadingWebm} type="video/webm" />
              </video>
            )}
          </div>

          {/* bottom UI overlay */}
          <div className="loading-ui">
            <div className="loading-container">
              <div className="loading-bg"></div>

              <div
                className="loading-fill-wrapper"
                style={{
                  WebkitMaskImage: `linear-gradient(to right, #000 ${progress}%, transparent ${progress}%)`,
                  WebkitMaskRepeat: "no-repeat",
                  WebkitMaskSize: "100% 100%",
                  WebkitMaskPosition: "0 0",
                  maskImage: `linear-gradient(to right, #000 ${progress}%, transparent ${progress}%)`,
                  maskRepeat: "no-repeat",
                  maskSize: "100% 100%",
                  maskPosition: "0 0",
                }}
              >
                <div className="loading-fill"></div>
              </div>
            </div>

            <div className="text-loading">Loading {progress}%</div>
          </div>
        </div>
      )}

      {/* unity mobile keyboard bridge input (kept) */}
      <input
        ref={inputRef}
        type="text"
        style={{
          position: "absolute",
          bottom: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 9999,
          opacity: keyboardOpen ? 1 : 0,
          pointerEvents: "none",
        }}
        onFocus={() => setKeyboardOpen(true)}
        onBlur={() => setKeyboardOpen(false)}
        onChange={(e) => {
          if (target.gameObj && target.method) {
            sendMessageRef.current?.(target.gameObj, target.method, e.target.value);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            inputRef.current.blur();
            setKeyboardOpen(false);
          }
        }}
      />

      {showFlipOverlay && <div className="flip-overlay"></div>}
    </div>
  );
}

export default UnityGame;
